import Head from 'next/head';
import React from 'react';
import { gql } from '@apollo/client';
import type { GetStaticPaths, GetStaticProps } from 'next';

import Layout from '../../../components/layout';
import ArticleResultsList from '../../../components/article/results-list';
import { ListingHeader } from '../../../components/article/ListingHeader';
import { client, getArticlesByAuthorNameQuery } from '../../../apollo-client';
import { FFH_HOSTNAME, SITE_NAME } from '@lib/constants';
import { resolveWordPressAuthorHandle } from '@lib/listings/legacy-handles';
import {
  buildAuthorPath,
  normaliseSanityListingItem
} from '@lib/listings/normalise';
import type { WordPressPostsData } from '@lib/listings/normalise';
import type { ListingItem } from '@lib/listings/types';
import {
  buildSanityImageUrl,
  getAuthor,
  getListingCardsByAuthor
} from '@lib/sanity';
import type { SanityAuthor } from '@lib/sanity';

export const REVALIDATE_SECONDS = 600;
export const NOT_FOUND_REVALIDATE_SECONDS = 600;
const AVATAR_WIDTH = 160;

export type AuthorPageProps = {
  articlesList: WordPressPostsData | null;
  author: SanityAuthor | null;
  sanityItems: ListingItem[];
  slug: string;
};

const Author = ({
  articlesList,
  author,
  sanityItems,
  slug
}: AuthorPageProps) => {
  const wordPressAuthorName =
    articlesList?.posts?.edges?.[0]?.node.author?.node?.name;
  const name = author?.name ?? wordPressAuthorName ?? slug;
  const canonicalUrl = `${FFH_HOSTNAME}${buildAuthorPath(slug)}`;
  const avatarUrl = author?.avatar?.asset
    ? buildSanityImageUrl(author.avatar)
        .width(AVATAR_WIDTH)
        .height(AVATAR_WIDTH)
        .url()
    : undefined;

  return (
    <div>
      <Head>
        <title>{`${name} | ${SITE_NAME}`}</title>
        {author?.bio && <meta name="description" content={author.bio} />}
        <link rel="canonical" href={canonicalUrl} key="canonical" />
      </Head>

      <Layout>
        <div className="relative bg-gray-50 pt-10 pb-20 px-4 sm:px-6 lg:pt-14 lg:pb-10 lg:px-8">
          <div className="absolute">
            <div className="bg-white h-1/3 sm:h-2/3" />
          </div>
          <ListingHeader
            title={name}
            eyebrow="Articles by"
            description={author?.bio}
            avatarUrl={avatarUrl}
          />
          <ArticleResultsList
            initialData={articlesList ?? undefined}
            category={{ name: slug }}
            query={getArticlesByAuthorNameQuery(
              resolveWordPressAuthorHandle(slug)
            )}
            sanityItems={sanityItems}
            type="categories"
          />
        </div>
      </Layout>
    </div>
  );
};

export const getStaticProps: GetStaticProps<AuthorPageProps> = async ({
  params
}) => {
  const slug = params?.slug as string;
  // A renamed contributor's WordPress posts still sit under the old login.
  const wordPressHandle = resolveWordPressAuthorHandle(slug);

  try {
    // network-only: the module-level Apollo client persists across ISR
    // revalidations in a warm lambda, so cache-first would pin the list
    // to whatever the instance fetched first.
    const [{ data }, author, sanityCards] = await Promise.all([
      client.query({
        query: gql`
          ${getArticlesByAuthorNameQuery(wordPressHandle)}
        `,
        fetchPolicy: 'network-only'
      }),
      getAuthor(slug),
      getListingCardsByAuthor(slug)
    ]);

    const hasWordPressPosts = (data?.posts?.edges?.length ?? 0) > 0;
    if (!author && !hasWordPressPosts) {
      return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };
    }

    return {
      props: {
        articlesList: data,
        author,
        sanityItems: sanityCards.map(normaliseSanityListingItem),
        slug
      },
      revalidate: REVALIDATE_SECONDS
    };
  } catch (error) {
    console.error(`Failed to fetch articles for author "${slug}":`, error);
    // Rethrow so Next.js keeps serving the last good page during
    // background revalidation instead of caching a transient 404.
    throw error;
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

export default Author;
